import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Storefront, Image as ImageIcon, Ticket, Warning, ShoppingCart, Minus, Plus } from "@phosphor-icons/react";
import { Shell } from "@/components/Shell";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import api, { API } from "@/lib/api";
import { ColorSwatch } from "@/components/VariantPreview";

const money = (v) => `€ ${Number(v || 0).toFixed(2)}`;

export default function Shop() {
  const { t, user, authChecked, shopEnabled, addToCart, cartCount } = useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const didAutoOpen = useRef(false);
  const [products, setProducts] = useState([]);
  const [avail, setAvail] = useState(0);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState(false);
  const [sel, setSel] = useState(null); // product being configured
  const [color, setColor] = useState(null);
  const [motif, setMotif] = useState(null);
  const [qty, setQty] = useState(1);
  const [motifPath, setMotifPath] = useState("");
  const [motifPreview, setMotifPreview] = useState("");
  const [uploadingMotif, setUploadingMotif] = useState(false);

  useEffect(() => {
    if (!authChecked) return;
    if (!user) { navigate("/"); return; }
    if (!shopEnabled && !user.is_admin) { setBlocked(true); setLoading(false); return; }
    setBlocked(false);
    api.get("/shop/products")
      .then((r) => setProducts(r.data))
      .catch((e) => { if (e?.response?.status === 403) setBlocked(true); })
      .finally(() => setLoading(false));
    api.get("/shop/activation/available").then((r) => setAvail(r.data.available)).catch(() => {});
  }, [authChecked, user, shopEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const openProduct = (p) => {
    setSel(p);
    setColor(p.colors?.length ? p.colors[0] : null);
    setMotif(p.motifs?.length ? p.motifs[0] : null);
    setQty(p.min_qty || 1);
    setMotifPath(""); setMotifPreview(""); setUploadingMotif(false);
  };

  const onMotifPick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setMotifPreview(URL.createObjectURL(f));
    setUploadingMotif(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/shop/upload-motif", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMotifPath(r.data.image_path);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload fehlgeschlagen"); setMotifPath(""); }
    finally { setUploadingMotif(false); }
  };

  useEffect(() => {
    if (didAutoOpen.current || sel) return;
    if (params.get("buy") !== "activation" || !products.length) return;
    const p = products.find((x) => x.ptype === "digital" || (x.codes_count || 0) > 0) || products[0];
    if (p) { didAutoOpen.current = true; openProduct(p); }
  }, [products]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasSurcharge = (p) => (p.colors || []).some((c) => c.price_delta > 0) || (p.motifs || []).some((m) => m.price_delta > 0);
  const unitPrice = sel ? Number(sel.price || 0) + Number(color?.price_delta || 0) + Number(motif?.price_delta || 0) : 0;

  const confirmAdd = () => {
    if ((sel.colors?.length || 0) > 0 && !color) { toast.error(t("chooseColor")); return; }
    if ((sel.motifs?.length || 0) > 0 && !motif) { toast.error(t("chooseMotif")); return; }
    if (sel.custom_motif && !motifPath) { toast.error("Bitte lade dein Wunschmotiv hoch"); return; }
    const key = `${sel.id}|${color?.id || ""}|${motif?.id || ""}|${motifPath || ""}`;
    addToCart({
      key, product_id: sel.id, product_name: sel.name, image_path: sel.image_path,
      base_price: Number(sel.price || 0),
      color: color ? { id: color.id, name: color.name, hex: color.hex, image_path: color.image_path, special: color.special, price_delta: color.price_delta } : null,
      motif: motif ? { id: motif.id, name: motif.name, image_path: motif.image_path, price_delta: motif.price_delta } : null,
      motif_image_path: motifPath || null,
      unit_price: Number(unitPrice.toFixed(2)),
      quantity: qty, min_qty: sel.min_qty || 1, max_qty: sel.max_qty || 10,
    });
    toast.success(t("cartAdded"));
    setSel(null);
  };

  return (
    <Shell back="/dashboard" wide>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5 shop-biz" data-testid="shop-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Storefront size={30} weight="duotone" style={{ color: "var(--app-strong)" }} />
            <div>
              <h1 className="text-2xl font-heading font-semibold text-gray-900">{t("shopTitle")}</h1>
              <p className="font-body text-sm text-gray-500">{t("shopSubtitle")}</p>
              <p className="font-body text-[11px] text-gray-400 mt-1" data-testid="shop-tax-note">{t("taxNote")}</p>
            </div>
          </div>
          <button data-testid="cart-button" onClick={() => navigate("/cart")} className="relative sb-btn p-2.5 bg-white">
            <ShoppingCart size={22} weight="bold" />
            {cartCount > 0 && <span data-testid="cart-badge" className="absolute -top-1.5 -right-1.5 h-5 min-w-5 px-1 rounded-full text-[11px] font-heading font-bold text-white flex items-center justify-center border-2 border-gray-800" style={{ background: "var(--app-primary)" }}>{cartCount}</span>}
          </button>
        </div>

        {loading && <p className="font-body text-gray-500">…</p>}
        {!loading && blocked && (
          <div className="sb-card p-6 text-center" data-testid="shop-closed">
            <Warning size={36} weight="duotone" className="mx-auto text-amber-500" />
            <p className="font-body text-gray-700 mt-2">{t("shopClosed")}</p>
          </div>
        )}

        {!loading && !blocked && (
          <>
            {user?.is_admin && !shopEnabled && (
              <div className="rounded-2xl border-2 border-amber-500 px-3 py-2 font-body text-sm text-gray-800" style={{ background: "#FFFBEB" }}>{t("shopEnabledHint")}</div>
            )}
            {products.length === 0 && <p className="font-body text-sm text-gray-500" data-testid="shop-empty">{t("shopEmpty")}</p>}
            <div className="shop-grid" data-testid="shop-products">
              {products.map((p) => (
                <div key={p.id} data-testid={`shop-product-${p.id}`} className="sb-card p-4">
                  <div className="rounded-2xl border-2 border-gray-800 overflow-hidden bg-gray-50 aspect-video flex items-center justify-center mb-3">
                    {p.image_path ? <img src={`${API}/files/${p.image_path}`} alt={p.name} className="h-full w-full object-cover" /> : <ImageIcon size={40} className="text-gray-300" />}
                  </div>
                  <h2 className="font-heading font-semibold text-lg text-gray-900">{p.name}</h2>
                  <span data-testid={`ptype-badge-${p.id}`} className="inline-block mt-1 text-[11px] font-heading font-semibold px-2 py-0.5 rounded-full" style={p.ptype === "digital" ? { background: "#DBEAFE", color: "#1D4ED8" } : { background: "#DCFCE7", color: "#15803D" }}>{p.ptype === "digital" ? "💾 Digitales Produkt" : "📦 Physisches Produkt"}</span>
                  {p.description && <p className="font-body text-sm text-gray-600 mt-1 whitespace-pre-line">{p.description}</p>}
                  <div className="flex items-center gap-2 mt-2">
                    <Ticket size={16} weight="bold" style={{ color: "var(--app-strong)" }} />
                    <span className="font-body text-sm text-gray-700">{p.codes_count} {t("codesIncluded")}</span>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xl font-heading font-semibold text-gray-900">{hasSurcharge(p) ? `${t("priceFrom")} ` : ""}{money(p.price)}</span>
                    {p.ptype !== "digital" && avail < (p.codes_count || 1) ? (
                      <span data-testid={`soldout-${p.id}`} className="px-3 py-2 rounded-xl font-heading font-medium text-sm" style={{ background: "#FEE2E2", color: "#B91C1C" }}>{t("outOfStock")}</span>
                    ) : (
                      <button data-testid={`add-${p.id}`} onClick={() => openProduct(p)} className="sb-btn px-5 py-2.5 text-white font-medium flex items-center gap-1.5" style={{ background: "var(--app-primary)" }}><ShoppingCart size={16} weight="bold" />{t("addToCartLabel")}</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </motion.div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent data-testid="options-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{sel?.name}</DialogTitle>
            <DialogDescription>{t("addToCartLabel")}</DialogDescription>
          </DialogHeader>
          {sel && (
            <div className="space-y-4">
              {sel.colors?.length > 0 && (
                <div>
                  <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("chooseColor")}</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {sel.colors.map((c) => (
                      <button key={c.id} data-testid={`color-${c.id}`} onClick={() => setColor(c)}
                        className={`flex items-center gap-2 border-2 rounded-xl px-2.5 py-1.5 ${color?.id === c.id ? "border-gray-900" : "border-gray-300"}`} style={color?.id === c.id ? { boxShadow: "2px 2px 0 0 #1F2937" } : {}}>
                        <ColorSwatch color={c} size={20} />
                        <span className="font-body text-sm">{c.name}{c.price_delta > 0 ? ` (+${money(c.price_delta)})` : ""}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {sel.motifs?.length > 0 && (
                <div>
                  <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("chooseMotif")}</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {sel.motifs.map((m) => (
                      <button key={m.id} data-testid={`motif-${m.id}`} onClick={() => setMotif(m)}
                        className={`flex items-center gap-2 border-2 rounded-xl px-2.5 py-1.5 ${motif?.id === m.id ? "border-gray-900" : "border-gray-300"}`} style={motif?.id === m.id ? { boxShadow: "2px 2px 0 0 #1F2937" } : {}}>
                        {m.image_path ? <img src={`${API}/files/${m.image_path}`} alt={m.name} className="h-6 w-6 rounded object-cover border border-gray-400" /> : <ImageIcon size={18} className="text-gray-400" />}
                        <span className="font-body text-sm">{m.name || "Motiv"}{m.price_delta > 0 ? ` (+${money(m.price_delta)})` : ""}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {sel.custom_motif && (
                <div data-testid="custom-motif-wrap">
                  <label className="text-xs font-body text-gray-500 uppercase tracking-wider">Dein Wunschmotiv (JPG/PNG)</label>
                  <div className="flex items-center gap-4 mt-2">
                    <label className="sb-btn px-3 py-2 bg-white text-sm font-medium flex items-center gap-1 cursor-pointer shrink-0">
                      <input type="file" accept="image/png,image/jpeg" className="hidden" data-testid="custom-motif-input" onChange={onMotifPick} />
                      <ImageIcon size={16} weight="bold" />{uploadingMotif ? "Lädt…" : (motifPath ? "Bild ändern" : "Bild hochladen")}
                    </label>
                    <div className="h-20 w-20 rounded-full border-2 border-gray-300 overflow-hidden bg-gray-50 flex items-center justify-center shrink-0" data-testid="custom-motif-preview">
                      {motifPreview ? <img src={motifPreview} alt="Vorschau" className="h-full w-full object-cover" /> : <ImageIcon size={22} className="text-gray-300" />}
                    </div>
                  </div>
                  <p className="font-body text-[11px] text-gray-400 mt-1">So erscheint dein Motiv rund auf dem Buddy-Tag.</p>
                </div>
              )}
              <div className="flex items-center justify-between">
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("quantityLabel")}</label>
                <div className="flex items-center gap-3">
                  <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(sel.min_qty || 1, q - 1))} className="sb-btn p-2 bg-white"><Minus size={16} weight="bold" /></button>
                  <span data-testid="qty-value" className="font-heading font-semibold w-6 text-center">{qty}</span>
                  <button data-testid="qty-plus" onClick={() => setQty((q) => Math.min(sel.max_qty || 10, q + 1))} className="sb-btn p-2 bg-white"><Plus size={16} weight="bold" /></button>
                </div>
              </div>
              <div className="flex items-center justify-between border-t-2 border-gray-200 pt-3">
                <span className="font-body text-gray-600">{t("unitPriceLabel")}</span>
                <span data-testid="option-unit-price" className="text-xl font-heading font-semibold">{money(unitPrice)}</span>
              </div>
              {sel.delivery_time && <p className="font-body text-[11px] text-gray-500 -mt-2" data-testid="product-delivery-time-display">Lieferzeit: {sel.delivery_time}</p>}
              <button data-testid="add-to-cart-confirm" onClick={confirmAdd} className="sb-btn w-full py-3 text-white font-medium flex items-center justify-center gap-2" style={{ background: "var(--app-primary)" }}><ShoppingCart size={18} weight="bold" />{t("addLabel")}</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
